import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { AutoMountModel, type AutoMountComponent, type AutoMountManager } from '../auto-mount-manager';
import _ from 'lodash';

const V_IF = 'vIf';

/**
 * Vue3 版本的自动挂载管理器
 */
@injectable()
export class VueAutoMountManager implements AutoMountManager {
    constructor(
        @inject(AutoMountModel)
        private readonly _model: AutoMountModel,
    ) {}

    dispose(): void {
        this._model.update(() => ({
            loopForNames: [],
            componentMap: {},
            attrMap: {},
        }));
    }

    update(component: AutoMountComponent, $props: Record<string, any>, $listeners: Record<string, any>): void {
        if (!this._hasRegistered(component)) {
            this._register(component);
        }

        const uniqueName = this._getUniqueId(component)!;
        const props = _.omit($props, [V_IF, 'onCancel', 'onSubmit']);
        this._model.update((state) => {
            if (!state.attrMap[uniqueName]) {
                state.attrMap[uniqueName] = { $props: {}, $listeners: {} };
            }
            state.attrMap[uniqueName].$props = { ...state.attrMap[uniqueName].$props, ...props };
            state.attrMap[uniqueName].$listeners = { ...state.attrMap[uniqueName].$listeners, ...$listeners };
            return state;
        });
    }

    toggleOpenState(component: AutoMountComponent, open: boolean): void {
        if (!this._hasRegistered(component)) {
            return;
        }

        const name = this._getUniqueId(component)!;
        this._model.update((state) => {
            state.attrMap[name].vIf = open;
            return state;
        });
    }

    hasOpenDialog(): boolean {
        const modelData = this._model.modelData;
        return modelData.loopForNames.some((name) => modelData.attrMap[name].vIf);
    }

    unregister(component: AutoMountComponent): void {
        if (!this._hasRegistered(component)) {
            return;
        }

        const name = this._getUniqueId(component)!;

        this._componentMap.delete(component);

        this._model.update((state) => {
            state.loopForNames = state.loopForNames.filter((n) => n !== name);
            delete state.componentMap[name];
            delete state.attrMap[name];
            return state;
        });
    }

    private _register(component: AutoMountComponent): string {
        const name = _.uniqueId('auto-mount-');
        this._componentMap.set(component, name);
        this._model.update((state) => {
            state.loopForNames.push(name);
            state.componentMap[name] = component;
            return state;
        });
        return name;
    }

    private _componentMap: WeakMap<AutoMountComponent, string> = new WeakMap();

    private _hasRegistered(component: AutoMountComponent): boolean {
        return this._componentMap.has(component);
    }

    private _getUniqueId(component: AutoMountComponent): string | undefined {
        return this._componentMap.get(component);
    }
}
